import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { MessageSquare, Mail, FileText, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [stats, setStats] = useState({
    contacts: 0,
    subscribers: 0,
    blogPosts: 0,
    pendingContacts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch contacts
      const contactsRes = await axios.get(
        `${API}/contact/requests`,
        getAuthHeaders()
      );
      const contacts = contactsRes.data;
      const pendingContacts = contacts.filter(c => c.status === 'pending').length;

      // Fetch subscribers
      const subscribersRes = await axios.get(
        `${API}/newsletter/subscribers`,
        getAuthHeaders()
      );
      const activeSubscribers = subscribersRes.data.filter(s => s.status === 'active').length;

      // Fetch blog posts
      const blogRes = await axios.get(`${API}/blog/posts`);
      const publishedPosts = blogRes.data.filter(p => p.published).length;

      setStats({
        contacts: contacts.length,
        subscribers: activeSubscribers,
        blogPosts: publishedPosts,
        pendingContacts
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Demandes de Contact',
      value: stats.contacts,
      icon: MessageSquare,
      color: 'bg-blue-500',
      badge: stats.pendingContacts > 0 ? `${stats.pendingContacts} en attente` : null
    },
    {
      title: 'Abonnés Newsletter',
      value: stats.subscribers,
      icon: Mail,
      color: 'bg-green-500'
    },
    {
      title: 'Articles Publiés',
      value: stats.blogPosts,
      icon: FileText,
      color: 'bg-purple-500'
    },
    {
      title: 'Taux de Conversion',
      value: stats.contacts > 0 ? `${Math.round((stats.subscribers / stats.contacts) * 100)}%` : '0%',
      icon: TrendingUp,
      color: 'bg-orange-500'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-slate-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-2">Vue d'ensemble de votre activité</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  {stat.title}
                </CardTitle>
                <div className={`${stat.color} p-2 rounded-lg`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">
                  {stat.value}
                </div>
                {stat.badge && (
                  <p className="text-xs text-orange-600 mt-2 font-medium">
                    {stat.badge}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Actions Rapides</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <a
              href="/admin/contacts"
              className="block p-3 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <p className="font-medium text-slate-900">Gérer les contacts</p>
              <p className="text-sm text-slate-600">Répondre aux demandes en attente</p>
            </a>
            <a
              href="/admin/blog"
              className="block p-3 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <p className="font-medium text-slate-900">Nouvel article</p>
              <p className="text-sm text-slate-600">Créer un nouvel article de blog</p>
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activité Récente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-slate-600">Système opérationnel</span>
                </div>
                <span className="text-xs text-slate-400">Maintenant</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-slate-600">{stats.contacts} contacts total</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-slate-600">{stats.blogPosts} articles publiés</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
